import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarPlus,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  FileUp,
  MessageCircle,
  Package,
  Paperclip,
  Phone,
  Ruler,
  Store,
  Trash2,
  Truck,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useMedia } from "@/hooks/use-media";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Segmented, SegmentedButtons } from "@/components/ui/segmented";
import { StatusBadge } from "@/components/ui/status-badge";
import { ErrorState } from "@/components/ui/states";
import { Initials } from "@/components/ui/list-row";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CampoData from "@/components/common/CampoData";
import SceltaCliente from "@/components/common/SceltaCliente";
import OrariLiberi from "@/components/crm/OrariLiberi";
import { LavoroMontaggio } from "@/components/crm/Montaggio";
import { useConferma } from "@/hooks/use-conferma";
import { vociDi } from "@/lib/accessi";
import {
  TIPO_APPUNTAMENTO,
  arrivo,
  daSegnare,
  erroreDi,
  giornoDi,
  giornoLungo,
  giornoOra,
  intervallo,
  lunediDi,
  oggi,
  ora,
  passato,
  piuGiorni,
  statoAppuntamento,
  testaGiorno,
} from "@/lib/crm";
import { cn } from "@/lib/utils";

const TUTTI = "__tutti__";
const DEL_CLIENTE = "__cliente__";
const TIPI_A_MANO = ["visita_showroom", "rilievo", "consegna"];
const DURATE = [
  { value: "45", label: "45 min" },
  { value: "60", label: "1 ora" },
  { value: "90", label: "1 ora e ½" },
  { value: "120", label: "2 ore" },
  { value: "180", label: "3 ore" },
  { value: "240", label: "Mezza giornata" },
];
const ICONA_TIPO = { visita_showroom: Store, rilievo: Ruler, consegna: Truck, montaggio: Package };

/* ── Come si legge una cella ─────────────────────────────────────────────────
   Quattro stati, con colore E parola (mai il colore da solo): in programma, da segnare (una visita
   passata senza esito), fatto, no show o annullato (barrato). Le etichette delle celle sono a 12 px
   (decisione del 30-10), come la barra del telefono. */
const IN_AGENDA = ["programmato", "riprogrammato"];

function statoCella(a) {
  if (a.stato === "annullato") return { chiave: "saltato", parola: a.tipo === "visita_showroom" ? "Annullata" : "Annullato" };
  if (a.stato === "no_show") return { chiave: "saltato", parola: statoAppuntamento(a.stato, a.tipo).label };
  if (a.stato === "presentato") return { chiave: "fatto", parola: a.tipo === "visita_showroom" ? "Presentata" : "Fatto" };
  if (daSegnare(a)) return { chiave: "segnare", parola: "Da segnare" };
  return { chiave: "programma", parola: "In programma" };
}

const TONO_CELLA = {
  programma: "border-brand-soft bg-brand-subtle text-brand-ink",
  segnare: "border-warning-line bg-warning-bg text-warning-ink",
  fatto: "border-line bg-surface text-ink",
  saltato: "border-danger-line bg-danger-bg text-danger-ink",
};
const TONO_BADGE = { programma: "brand", segnare: "warning", fatto: "success", saltato: "danger" };

const minuti = (valore) => {
  const [h, m] = ora(valore).split(":").map(Number);
  return h * 60 + m;
};
const fineDi = (a) => (a.data_fine ? minuti(a.data_fine) || 24 * 60 : minuti(a.data_inizio) + 60);

/** «9:00–10:00», senza lo zero davanti all'ora. */
function fascia(a) {
  const t = (x) => ora(x).replace(/^0/, "");
  return a.data_fine ? `${t(a.data_inizio)}–${t(a.data_fine)}` : t(a.data_inizio);
}

/* ── La griglia oraria (computer): settimana = una colonna per giorno, giorno = una per persona ── */

/** Le corsie: gli appuntamenti che si sovrappongono stanno uno accanto all'altro, non uno sopra l'altro. */
function inCorsie(eventi) {
  const fuori = [];
  let gruppo = [];
  let fine = -1;
  const chiudi = () => {
    const n = Math.max(...gruppo.map((x) => x.corsia)) + 1;
    for (const x of gruppo) x.corsie = n;
  };
  for (const a of [...eventi].sort((x, y) => minuti(x.data_inizio) - minuti(y.data_inizio) || fineDi(y) - fineDi(x))) {
    const s = minuti(a.data_inizio);
    const e = Math.max(fineDi(a), s + 15);
    if (gruppo.length && s >= fine) {
      chiudi();
      gruppo = [];
      fine = -1;
    }
    const usate = new Set(gruppo.filter((x) => x.e > s).map((x) => x.corsia));
    let corsia = 0;
    while (usate.has(corsia)) corsia += 1;
    const x = { a, s, e, corsia };
    gruppo.push(x);
    fuori.push(x);
    fine = Math.max(fine, e);
  }
  if (gruppo.length) chiudi();
  return fuori;
}

function Squadra({ a }) {
  return a.squadra?.length ? (
    <span className="block truncate" data-testid={`squadra-${a.id}`}>
      {a.squadra.join(" + ")}
    </span>
  ) : null;
}

function Cella({ x, inizio, oraPx, onApri }) {
  const { a } = x;
  const st = statoCella(a);
  const Icona = ICONA_TIPO[a.tipo] || Store;
  const top = ((x.s - inizio * 60) * oraPx) / 60;
  const alto = Math.max(((x.e - x.s) * oraPx) / 60 - 3, 24);
  return (
    <button
      type="button"
      onClick={() => onApri(a.id)}
      data-testid={`appuntamento-${a.id}`}
      className={cn(
        "absolute flex flex-col items-stretch justify-start overflow-hidden rounded-sm border px-1.5 py-1 text-left text-caption leading-4 transition-[box-shadow] duration-150 ease-enter hover:shadow-overlay",
        "focus-visible:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand",
        TONO_CELLA[st.chiave],
      )}
      style={{ top, height: alto, left: `calc(${(x.corsia / x.corsie) * 100}% + 3px)`, width: `calc(${100 / x.corsie}% - 6px)` }}
    >
      <span className="flex items-center gap-1 font-bold tabular-nums">
        <Icona className="size-3 shrink-0" aria-hidden />
        <span className="truncate">{fascia(a)}</span>
      </span>
      <span className={cn("block truncate font-bold", st.chiave === "saltato" && "line-through")}>{a.cliente || "Senza cliente"}</span>
      <span className="block truncate font-normal">
        {st.parola}
        {a.sede ? ` · ${a.sede}` : ""}
      </span>
      <span className="font-normal">
        <Squadra a={a} />
      </span>
    </button>
  );
}

function useAdesso() {
  const [t, setT] = useState(() => Date.now());
  useEffect(() => {
    const i = setInterval(() => setT(Date.now()), 60000);
    return () => clearInterval(i);
  }, []);
  return t;
}

function Griglia({ colonne, oraPx, onApri, conLinea }) {
  const adesso = useAdesso();
  const tutti = colonne.flatMap((c) => c.eventi);
  const inizio = Math.min(8, ...tutti.map((a) => Math.floor(minuti(a.data_inizio) / 60)));
  const fine = Math.max(19, ...tutti.map((a) => Math.ceil(fineDi(a) / 60)));
  const ore = Array.from({ length: fine - inizio }, (_, i) => inizio + i);
  const oraAdesso = (() => {
    const [h, m] = ora(new Date(adesso).toISOString()).split(":").map(Number);
    return h * 60 + m;
  })();
  const lineaTop = ((oraAdesso - inizio * 60) * oraPx) / 60;
  const lineaVisibile = oraAdesso >= inizio * 60 && oraAdesso <= fine * 60;
  return (
    <div className="overflow-hidden rounded-xl border border-line bg-surface">
      <div className="grid border-b border-line" style={{ gridTemplateColumns: `3.5rem repeat(${colonne.length}, minmax(0, 1fr))` }}>
        <span aria-hidden />
        {colonne.map((c) => (
          <div key={c.chiave} className={cn("min-w-0 border-l border-line-subtle", c.evidenzia && "bg-brand-subtle/50")}>
            {c.testa}
          </div>
        ))}
      </div>
      <div
        className="relative grid"
        style={{ gridTemplateColumns: `3.5rem repeat(${colonne.length}, minmax(0, 1fr))`, height: ore.length * oraPx }}
      >
        <div className="relative">
          {ore.map((h, i) => (
            <span
              key={h}
              className="absolute right-2 -translate-y-1/2 text-caption font-normal tabular-nums text-ink-2"
              style={{ top: i * oraPx + (i === 0 ? 8 : 0) }}
            >
              {h}:00
            </span>
          ))}
        </div>
        {colonne.map((c) => (
          <div
            key={c.chiave}
            className={cn("relative border-l border-line-subtle", c.evidenzia && "bg-brand-subtle/40")}
            data-colonna-giorno={c.chiave}
          >
            {ore.map((h, i) => (
              <span key={h} className="absolute inset-x-0 border-t border-line-subtle" style={{ top: i * oraPx }} aria-hidden />
            ))}
            {inCorsie(c.eventi).map((x) => (
              <Cella key={x.a.id} x={x} inizio={inizio} oraPx={oraPx} onApri={onApri} />
            ))}
            {conLinea(c) && lineaVisibile ? (
              <span className="pointer-events-none absolute inset-x-0 z-10 h-0.5 bg-brand" style={{ top: lineaTop }} aria-hidden>
                <span className="absolute -left-1 -top-[3px] size-2 rounded-full bg-brand" />
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── L'agenda (telefono): le visite in fila, con l'esito a un tocco ─────────── */
function RigaAgenda({ a, onApri, onEsito, busy }) {
  const st = statoCella(a);
  const Icona = ICONA_TIPO[a.tipo] || Store;
  const segnare = st.chiave === "segnare";
  return (
    <li className={cn("rounded-lg border", segnare ? "border-warning-line bg-warning-bg" : "border-line bg-surface")}>
      <button
        type="button"
        onClick={() => onApri(a.id)}
        data-testid={`appuntamento-${a.id}`}
        className="grid w-full grid-cols-[3.25rem_minmax(0,1fr)] gap-3 rounded-lg p-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand"
      >
        <span className="tabular-nums">
          <span className={cn("block font-bold", segnare ? "text-warning-ink" : "text-brand-ink")}>
            {ora(a.data_inizio).replace(/^0/, "")}
          </span>
          {a.data_fine ? <span className="block text-small text-ink-2">{ora(a.data_fine).replace(/^0/, "")}</span> : null}
        </span>
        <span className="min-w-0">
          <span className={cn("block truncate font-bold text-ink-strong", st.chiave === "saltato" && "line-through")}>
            {a.cliente || "Senza cliente"}
          </span>
          <span className="flex min-w-0 items-center gap-1.5 text-small text-ink-2">
            <Icona className="size-3.5 shrink-0" aria-hidden />
            <span className="truncate">{[a.tipo_label, a.sede, a.venditore].filter(Boolean).join(" · ")}</span>
          </span>
          <span className="text-small text-ink-2">
            <Squadra a={a} />
          </span>
          <span
            className={cn(
              "block text-small font-semibold",
              st.chiave === "segnare"
                ? "text-warning-ink"
                : st.chiave === "fatto"
                  ? "text-success-ink"
                  : st.chiave === "saltato"
                    ? "text-danger-ink"
                    : "text-brand-ink",
            )}
          >
            {segnare ? "Com'è andata?" : st.parola}
          </span>
        </span>
      </button>
      {segnare ? (
        <div className="grid grid-cols-2 gap-inside px-3 pb-3">
          <Button disabled={busy} onClick={() => onEsito(a, "presentato")} data-testid={`agenda-presentata-${a.id}`}>
            Presentata
          </Button>
          <Button
            variant="outline"
            disabled={busy}
            className="text-danger-ink"
            onClick={() => onEsito(a, "no_show")}
            data-testid={`agenda-noshow-${a.id}`}
          >
            No show
          </Button>
        </div>
      ) : null}
    </li>
  );
}

/* ── Nuovo appuntamento ──────────────────────────────────────────────────── */
export function NuovoAppuntamentoDialog({ open, onOpenChange, onCreato, sedi, giornoIniziale, clienteIniziale, calDalCrm }) {
  const [cliente, setCliente] = useState(null);
  const [tipo, setTipo] = useState("visita_showroom");
  const [giorno, setGiorno] = useState(giornoIniziale);
  const [orario, setOrario] = useState("10:00");
  const [durata, setDurata] = useState("60");
  const [sede, setSede] = useState(DEL_CLIENTE);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState(null);
  const [giro, setGiro] = useState(0);
  const [conCal, setConCal] = useState(false);
  const visita = tipo === "visita_showroom" && calDalCrm;

  useEffect(() => {
    if (open) {
      setErrore(null);
      setCliente(clienteIniziale || null);
      setTipo("visita_showroom");
      setGiorno(giornoIniziale < oggi() ? oggi() : giornoIniziale);
      setOrario(calDalCrm ? "" : "10:00");
      setDurata("60");
      setSede(DEL_CLIENTE);
      setNote("");
      setConCal(false);
    }
  }, [open, giornoIniziale, clienteIniziale]); // eslint-disable-line react-hooks/exhaustive-deps -- calDalCrm arriva col calendario: non deve azzerare il modulo

  const sedeScelta = sede === DEL_CLIENTE ? "" : sede;
  const giorni = Array.from({ length: 8 }, (_, i) => piuGiorni(oggi(), i));
  const conConferma = visita && conCal;

  const salva = async (e) => {
    e.preventDefault();
    if (!cliente) return;
    setBusy(true);
    setErrore(null);
    try {
      const r = await api.post("/calendario", {
        cliente_id: cliente.id,
        tipo,
        giorno,
        ora: orario,
        durata_min: Number(durata),
        sede_id: sedeScelta || null,
        note: note.trim() || null,
      });
      onOpenChange(false);
      onCreato(r.data);
    } catch (err) {
      /* Il dialogo resta aperto col perché: se Cal non l'ha presa, la visita non c'è. Gli orari si rileggono:
         dopo un «occupato» quello scelto non c'è più, e i più vicini sono quelli che restano. */
      setErrore(erroreDi(err));
      setGiro((g) => g + 1);
    } finally {
      setBusy(false);
    }
  };

  const sedeNome = sedi.find((s) => s.id === sedeScelta)?.nome;
  const riepilogo = giorno && orario ? `${giornoLungo(giorno)}, ${orario}` : giorno ? giornoLungo(giorno) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="nuovo-appuntamento-modal" className="max-h-[92dvh] overflow-y-auto sm:max-w-[860px]">
        <form onSubmit={salva} className="grid gap-card">
          <DialogHeader>
            <DialogTitle>Nuovo appuntamento</DialogTitle>
            <DialogDescription>
              {calDalCrm
                ? "Scegli chi, cosa e quando. Una visita in showroom prende l'orario anche su Cal della sede: gli orari già occupati non si scelgono."
                : "Una visita fissata al telefono, un rilievo, una consegna. Le prenotazioni da Cal arrivano da sole."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-card md:grid-cols-2 md:gap-x-section">
            <div className="grid content-start gap-card">
              <div className="grid gap-inside">
                <Label>Cliente</Label>
                {cliente ? (
                  <div className="flex items-center gap-3 rounded-sm border border-line p-3">
                    <Initials name={cliente.nome} size={36} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold text-ink-strong">{cliente.nome}</span>
                      {cliente.telefono ? <span className="block truncate text-small text-ink-2">{cliente.telefono}</span> : null}
                    </span>
                    <Button type="button" variant="ghost" onClick={() => setCliente(null)} data-testid="nuovo-app-cliente-cambia">
                      Cambia
                    </Button>
                  </div>
                ) : (
                  <SceltaCliente valore={null} onScegli={setCliente} testId="nuovo-app-cliente" />
                )}
              </div>
              <fieldset className="grid gap-inside">
                <legend className="mb-inside text-body-strong text-ink">Che appuntamento è</legend>
                <div className="grid grid-cols-3 gap-inside">
                  {TIPI_A_MANO.map((t) => {
                    const Icona = ICONA_TIPO[t];
                    return (
                      <button
                        key={t}
                        type="button"
                        aria-pressed={tipo === t}
                        onClick={() => setTipo(t)}
                        data-testid={`nuovo-app-tipo-${t}`}
                        className={cn(
                          "flex min-h-12 items-center justify-center gap-1.5 rounded-sm border px-2 text-small font-semibold transition-colors duration-150 ease-enter",
                          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
                          tipo === t
                            ? "border-brand bg-brand font-bold text-on-brand"
                            : "border-line bg-surface text-ink hover:bg-surface-2",
                        )}
                      >
                        <Icona className="size-4 shrink-0" aria-hidden />
                        <span className="truncate">{t === "consegna" ? "Consegna" : TIPO_APPUNTAMENTO[t]}</span>
                      </button>
                    );
                  })}
                </div>
              </fieldset>
              {sedi.length ? (
                <div className="grid gap-inside">
                  <Label id="na-sede">In quale showroom</Label>
                  <Segmented
                    aria-label="In quale showroom"
                    value={sede}
                    onValueChange={setSede}
                    options={[{ value: DEL_CLIENTE, label: "Quello del cliente" }, ...sedi.map((s) => ({ value: s.id, label: s.nome }))]}
                    className="flex w-full flex-wrap [&>*]:flex-1"
                  />
                </div>
              ) : null}
              <div className="grid gap-inside">
                <Label htmlFor="na-note">
                  Nota <span className="font-normal text-ink-3">· la vede chi lo segue</span>
                </Label>
                <Textarea id="na-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
            </div>

            <div className="grid content-start gap-card">
              <fieldset className="grid gap-inside">
                <legend className="mb-inside text-body-strong text-ink">Giorno</legend>
                <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-8">
                  {giorni.map((g) => {
                    const [nomeG, numero] = testaGiorno(g).split(" ");
                    return (
                      <button
                        key={g}
                        type="button"
                        aria-pressed={giorno === g}
                        aria-label={giornoLungo(g)}
                        onClick={() => setGiorno(g)}
                        className={cn(
                          "flex min-h-14 flex-col items-center justify-center rounded-sm border transition-colors duration-150 ease-enter",
                          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
                          giorno === g ? "border-brand bg-brand text-on-brand" : "border-line bg-surface text-ink hover:bg-surface-2",
                        )}
                      >
                        <span className="text-caption font-medium">{nomeG}</span>
                        <span className="font-figure text-h4">{numero}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-group">
                  <Label htmlFor="na-giorno" className="font-normal text-ink-2">
                    Un altro giorno
                  </Label>
                  <CampoData id="na-giorno" value={giorno} onChange={(e) => setGiorno(e.target.value)} testId="nuovo-app-giorno" required />
                </div>
              </fieldset>
              {visita ? (
                <OrariLiberi
                  id="na-ora"
                  testId="nuovo-app-ora"
                  giorno={giorno}
                  sedeId={sedeScelta || undefined}
                  clienteId={cliente?.id}
                  pronto={Boolean(sedeScelta || cliente)}
                  ricarica={giro}
                  valore={orario}
                  onScegli={setOrario}
                  onCal={setConCal}
                />
              ) : (
                <>
                  <div className="grid gap-inside">
                    <Label htmlFor="na-ora">Ora</Label>
                    <Input
                      id="na-ora"
                      type="time"
                      step={900}
                      value={orario}
                      onChange={(e) => setOrario(e.target.value)}
                      required
                      data-testid="nuovo-app-ora"
                    />
                  </div>
                  <div className="grid gap-inside">
                    <Label id="na-durata">Quanto dura</Label>
                    <Segmented
                      aria-label="Quanto dura"
                      value={durata}
                      onValueChange={setDurata}
                      options={DURATE}
                      className="flex w-full flex-wrap [&>*]:flex-1"
                    />
                  </div>
                </>
              )}
              {visita ? (
                conConferma ? (
                  <div className="flex gap-3 rounded-lg bg-surface-2 p-3" data-testid="nuovo-app-whatsapp">
                    <MessageCircle className="mt-0.5 size-5 shrink-0 text-success" aria-hidden />
                    <p className="text-small text-ink">
                      <span className="block font-semibold text-ink-strong">La conferma parte su WhatsApp</span>
                      Parte da sola (di notte aspetta il mattino), e poi i promemoria prima della visita. La durata è quella della visita su
                      Cal.
                    </p>
                  </div>
                ) : (
                  <p className="text-small text-ink-2">La durata è quella della visita su Cal.</p>
                )
              ) : null}
              {errore ? (
                <p role="alert" className="rounded-sm bg-danger-bg px-3 py-2.5 text-small text-danger-ink" data-testid="nuovo-app-errore">
                  {errore}
                </p>
              ) : null}
            </div>
          </div>
          <DialogFooter className="items-center gap-inside sm:justify-between">
            <p className="min-w-0 text-small text-ink-2 max-sm:order-last" aria-live="polite">
              {riepilogo ? <span className="block font-semibold text-ink-strong first-letter:uppercase">{riepilogo}</span> : null}
              {[TIPO_APPUNTAMENTO[tipo], sedeNome, cliente?.nome].filter(Boolean).join(" · ")}
            </p>
            <div className="flex flex-col-reverse gap-inside sm:flex-row">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Annulla
              </Button>
              {/* Finché il calendario non ha detto se Cal è collegato, i testi non lo sanno: si aspetta. */}
              <Button
                type="submit"
                disabled={busy || !cliente || !giorno || !orario || calDalCrm === undefined}
                data-testid="nuovo-app-salva"
              >
                {busy ? "Salvo…" : conConferma ? "Fissa e manda la conferma" : "Fissa l'appuntamento"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── Il dettaglio: l'esito, il cliente, i messaggi automatici, i file ─────── */
/* In showroom l'esito è la persona (è venuta o no); fuori, è il lavoro (fatto o no). */
function esitiPer(tipo) {
  const showroom = tipo === "visita_showroom";
  return [
    { stato: "presentato", label: showroom ? "Presentata" : "Fatto", icona: Check },
    { stato: "no_show", label: showroom ? "No show" : "Non fatto", icona: X },
    { stato: "annullato", label: showroom ? "Annullata" : "Annullato", icona: Clock },
  ];
}

const CONSEGNA = { read: "letto", delivered: "recapitato", sent: "consegnato a WhatsApp", accepted: "inviato", failed: "non recapitato" };

function MessaggiAutomatici({ lista }) {
  if (!lista?.length) return null;
  return (
    <section className="grid gap-inside border-t border-line-subtle pt-group" data-testid="dettaglio-messaggi">
      <h3 className="text-body-strong text-ink-strong">Messaggi automatici</h3>
      <ul className="grid gap-inside">
        {lista.map((m) => {
          const partito = m.stato === "inviato";
          const male = m.stato === "errore" || m.consegna === "failed";
          return (
            <li key={m.chiave} className="flex items-center gap-3" data-testid={`dettaglio-messaggio-${m.chiave}`}>
              <span
                className={cn(
                  "inline-flex size-6 shrink-0 items-center justify-center rounded-full",
                  male ? "bg-danger-bg text-danger-ink" : partito ? "bg-success-bg text-success-ink" : "bg-surface-3 text-ink-2",
                )}
                aria-hidden
              >
                {male ? <X className="size-3.5" /> : partito ? <Check className="size-3.5" /> : <Clock className="size-3.5" />}
              </span>
              <span className="min-w-0 flex-1 truncate text-ink">{m.nome}</span>
              <span className="shrink-0 text-right text-small text-ink-2">
                {m.stato === "in_attesa"
                  ? `in coda, ${giornoOra(m.quando)}`
                  : m.stato === "saltato"
                    ? "non partito"
                    : m.stato === "errore"
                      ? "errore, non partito"
                      : `partito ${giornoOra(m.quando)}${m.consegna ? ` · ${CONSEGNA[m.consegna] || m.consegna}` : ""}`}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Allegati({ app, onCambiato }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const { chiedi, dialogoConferma } = useConferma();

  const carica = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const dati = new FormData();
    dati.append("file", file);
    dati.append("tipo", app.tipo === "rilievo" ? "schema_impianti" : "altro");
    setBusy(true);
    try {
      await api.post(`/calendario/${app.id}/allegati`, dati);
      toast.success("File allegato alla scheda del cliente");
      onCambiato();
    } catch (err) {
      toast.error(erroreDi(err));
    } finally {
      setBusy(false);
    }
  };
  const apri = async (id) => {
    /* L'indirizzo si chiede adesso e scade in un minuto: sono foto e schemi della
       casa di una persona, e un link che gira per sempre non va bene. */
    try {
      const r = await api.get(`/calendario/allegati/${id}/link`);
      window.open(r.data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(erroreDi(err));
    }
  };
  const togli = async (a) => {
    if (
      !(await chiedi({
        titolo: "Togliere il file?",
        descrizione: `«${a.nome}» sparisce dalla scheda del cliente.`,
        verbo: "Togli il file",
      }))
    )
      return;
    try {
      await api.delete(`/calendario/allegati/${a.id}`);
      onCambiato();
    } catch (err) {
      toast.error(erroreDi(err));
    }
  };

  return (
    <section className="grid gap-inside border-t border-line-subtle pt-group" data-testid="allegati">
      <div className="flex items-center justify-between gap-group">
        <h3 className="text-body-strong text-ink-strong">{app.tipo === "rilievo" ? "Schema impianti" : "Allegati"}</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-11 md:h-9"
          disabled={busy}
          onClick={() => input.current?.click()}
          data-testid="allega-file"
        >
          <FileUp />
          {busy ? "Carico…" : "Allega"}
        </Button>
        <input
          ref={input}
          type="file"
          className="sr-only"
          accept=".pdf,image/*"
          onChange={carica}
          data-testid="allega-input"
          tabIndex={-1}
        />
      </div>
      {app.allegati?.length ? (
        <ul className="grid gap-1.5">
          {app.allegati.map((f) => (
            <li key={f.id} className="flex min-h-11 items-center gap-inside rounded-sm border border-line px-2">
              <Paperclip className="size-4 shrink-0 text-ink-3" aria-hidden />
              <button
                type="button"
                onClick={() => apri(f.id)}
                className="min-w-0 flex-1 truncate text-left font-semibold text-brand-ink hover:underline"
                data-testid={`allegato-${f.id}`}
              >
                {f.nome}
              </button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-11 md:size-9"
                onClick={() => togli(f)}
                aria-label={`Togli ${f.nome}`}
              >
                <Trash2 />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-small text-ink-2">
          {app.tipo === "rilievo"
            ? "Allega qui lo schema impianti: resta sulla scheda del cliente."
            : "Nessun file. Quello che alleghi resta sulla scheda del cliente."}
        </p>
      )}
      {dialogoConferma}
    </section>
  );
}

/* Spostare una visita in agenda: su Cal prima (la persona riceve l'orario nuovo da lì), qui dopo. */
function Sposta({ app, onFatto, onErrore, onChiudi }) {
  const [giorno, setGiorno] = useState(giornoDi(app.data_inizio));
  /* Una visita su Cal si sposta scegliendo fra gli orari liberi: si parte senza ora, non da quella di prima. */
  const conOrari = app.tipo === "visita_showroom" && app.cal_dal_crm;
  const [orario, setOrario] = useState(conOrari ? "" : ora(app.data_inizio));
  const [busy, setBusy] = useState(false);
  const [giro, setGiro] = useState(0);

  const salva = async (e) => {
    e.preventDefault();
    setBusy(true);
    onErrore(null);
    try {
      const r = await api.patch(`/calendario/${app.id}`, { giorno, ora: orario });
      toast.success(r.data.da_cal ? "Spostata, anche su Cal: lì c'è l'orario nuovo." : "Spostato");
      onFatto(r.data);
    } catch (err) {
      onErrore(erroreDi(err));
      setGiro((g) => g + 1); // dopo un «occupato» la lista era vecchia
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={salva} className="grid gap-group rounded-lg border border-line bg-surface-2 p-4" data-testid="sposta">
      <div className="grid gap-group sm:grid-cols-2">
        <div className="grid content-start gap-inside">
          <Label htmlFor="sp-giorno">Giorno</Label>
          <CampoData id="sp-giorno" value={giorno} onChange={(e) => setGiorno(e.target.value)} testId="sposta-giorno" required />
        </div>
        {conOrari ? null : (
          <div className="grid content-start gap-inside">
            <Label htmlFor="sp-ora">Ora</Label>
            <Input
              id="sp-ora"
              type="time"
              step={900}
              value={orario}
              onChange={(e) => setOrario(e.target.value)}
              required
              data-testid="sposta-ora"
            />
          </div>
        )}
      </div>
      {conOrari ? (
        <OrariLiberi
          id="sp-ora"
          testId="sposta-ora"
          giorno={giorno}
          appuntamentoId={app.id}
          ricarica={giro}
          valore={orario}
          onScegli={setOrario}
        />
      ) : null}
      {app.tipo === "visita_showroom" && app.cal_dal_crm ? (
        <p className="text-small text-ink-2">Si sposta anche su Cal: il vecchio orario torna libero online.</p>
      ) : null}
      <div className="flex flex-wrap justify-end gap-inside">
        <Button type="button" variant="outline" onClick={onChiudi}>
          Lascia com'è
        </Button>
        <Button type="submit" disabled={busy || !giorno || !orario} data-testid="sposta-salva">
          {busy ? "Sposto…" : "Sposta"}
        </Button>
      </div>
    </form>
  );
}

function Dettaglio({ id, onChiudi, onCambiato }) {
  const [app, setApp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sposta, setSposta] = useState(false);
  const [errore, setErrore] = useState(null);
  const { chiedi, dialogoConferma } = useConferma();
  /* Un errore toglie il «fatto» verde del gesto prima: i due insieme si contraddicono. */
  const mostraErrore = (m) => {
    if (m) toast.dismiss();
    setErrore(m);
  };

  const carica = useCallback(() => {
    if (!id) return;
    api
      .get(`/calendario/${id}`)
      .then((r) => setApp(r.data))
      .catch((e) => {
        toast.error(erroreDi(e));
        onChiudi();
      });
  }, [id, onChiudi]);

  useEffect(() => {
    setApp(null);
    setSposta(false);
    setErrore(null);
    carica();
  }, [carica]);

  const esito = async (stato) => {
    if (stato === "annullato") {
      const ok = await chiedi({
        titolo: "Segnarlo annullato?",
        descrizione: !app.da_cal
          ? "L'appuntamento resta in calendario, barrato."
          : app.cal_dal_crm
            ? "Si annulla anche su Cal: l'orario torna libero online e i messaggi in coda si fermano. Su Cal non si riapre."
            : "I messaggi in coda si fermano. L'orario su Cal resta occupato finché non lo cancelli anche lì.",
        verbo: "Segna annullato",
      });
      if (!ok) return;
    }
    setBusy(true);
    setErrore(null);
    try {
      await api.patch(`/calendario/${app.id}`, { stato });
      toast.success(
        stato === "no_show" && app.da_cal
          ? "Segnato: non è venuto. Il messaggio di recupero va in coda."
          : stato === "annullato" && app.da_cal && app.cal_dal_crm
            ? "Annullata, anche su Cal."
            : "Esito salvato",
      );
      carica();
      onCambiato();
    } catch (err) {
      /* Se Cal non risponde il gesto non è avvenuto: lo si legge qui, non solo in un avviso che sparisce. */
      mostraErrore(erroreDi(err));
    } finally {
      setBusy(false);
    }
  };

  const inAgenda = app && IN_AGENDA.includes(app.stato);
  /* Un giorno di consegna e montaggio lo sposta e lo annulla chi lo organizza (0047): al venditore resta l'esito. */
  const delLavoro = Boolean(app?.lavoro_id) && !app?.puo_organizzare;
  const st = app ? statoCella(app) : null;
  const minutiPassati = app && daSegnare(app) ? Math.round((Date.now() - new Date(app.data_inizio).getTime()) / 60000) : 0;
  const passatoDa =
    minutiPassati < 60
      ? `${minutiPassati} minuti`
      : minutiPassati < 1440
        ? `${Math.round(minutiPassati / 60)} ore`
        : `${Math.round(minutiPassati / 1440)} giorni`;

  return (
    <Sheet open={Boolean(id)} onOpenChange={(v) => !v && onChiudi()}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-[520px]" data-testid="dettaglio-appuntamento">
        {!app ? (
          <div className="grid gap-group p-card" aria-busy="true">
            <SheetTitle className="sr-only">Appuntamento</SheetTitle>
            <SheetDescription className="sr-only">Caricamento</SheetDescription>
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-8 w-56" />
            <Skeleton className="h-28 w-full" />
          </div>
        ) : (
          <>
            <div className="grid flex-1 content-start gap-card p-card">
              <div className="grid gap-inside pr-10">
                <StatusBadge tone={TONO_BADGE[st.chiave]} className="justify-self-start">
                  {st.parola}
                </StatusBadge>
                <SheetTitle className="font-display text-h2 text-ink-strong">{app.cliente || "Senza cliente"}</SheetTitle>
                <SheetDescription className="text-ink-2">
                  {app.tipo_label}
                  {app.da_cal ? " · da Cal" : ""}
                  {app.data_inizio ? ` · ${giornoLungo(giornoDi(app.data_inizio))}, ${fascia(app)}` : ""}
                </SheetDescription>
              </div>

              {inAgenda && sposta ? (
                <Sposta
                  app={app}
                  onErrore={mostraErrore}
                  onChiudi={() => setSposta(false)}
                  onFatto={(nuovo) => {
                    setSposta(false);
                    setApp((a) => ({ ...a, ...nuovo }));
                    onCambiato(nuovo);
                  }}
                />
              ) : null}

              {errore ? (
                <p role="alert" className="rounded-sm bg-danger-bg px-3 py-2.5 text-small text-danger-ink" data-testid="dettaglio-errore">
                  {errore}
                </p>
              ) : null}

              <section
                className={cn(
                  "grid gap-group rounded-lg p-4",
                  st.chiave === "segnare" ? "border border-warning-line bg-warning-bg" : "bg-surface-2",
                )}
              >
                <div>
                  <h3 className={cn("font-bold", st.chiave === "segnare" ? "text-warning-ink" : "text-ink-strong")}>Com'è andata?</h3>
                  {st.chiave === "segnare" ? (
                    <p className="text-small text-warning-ink">
                      L'orario è passato da {passatoDa}. Un tocco e la scheda si aggiorna da sola.
                    </p>
                  ) : null}
                </div>
                <div className="grid grid-cols-3 gap-inside" role="group" aria-label="Esito">
                  {esitiPer(app.tipo).map((e) => {
                    const Icona = e.icona;
                    const attivo = app.stato === e.stato;
                    /* Una visita annullata su Cal non si riapre: si fissa una nuova. */
                    const bloccato =
                      (attivo && e.stato === "annullato" && app.da_cal && app.cal_dal_crm) ||
                      (delLavoro && (e.stato === "annullato" || app.stato === "annullato"));
                    const principale = e.stato === "presentato" && st.chiave === "segnare";
                    return (
                      <button
                        key={e.stato}
                        type="button"
                        disabled={busy || bloccato}
                        aria-pressed={attivo}
                        onClick={() => esito(attivo ? "programmato" : e.stato)}
                        data-testid={`esito-${e.stato}`}
                        className={cn(
                          "flex min-h-12 items-center justify-center gap-1.5 rounded-sm border px-2 font-bold transition-colors duration-150 ease-enter",
                          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-60",
                          attivo
                            ? "border-brand bg-brand-subtle text-brand-ink"
                            : principale
                              ? "border-brand bg-brand text-on-brand hover:bg-brand-hover"
                              : e.stato === "no_show"
                                ? "border-line-input bg-surface text-danger-ink hover:bg-surface-2"
                                : "border-line-input bg-surface text-ink hover:bg-surface-2",
                        )}
                      >
                        <Icona className="size-4" aria-hidden />
                        {e.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-small text-ink-2">
                  {delLavoro && app.stato === "annullato"
                    ? "Annullato da chi organizza il lavoro: per rimetterlo in agenda, chiedi a loro."
                    : app.stato === "annullato" && app.da_cal && app.cal_dal_crm
                      ? "Annullata anche su Cal: per rivederlo, fissa una visita nuova."
                      : !IN_AGENDA.includes(app.stato)
                        ? "Tocca di nuovo l'esito scelto per toglierlo."
                        : app.tipo === "visita_showroom"
                          ? "Ancora senza esito: il cliente resta in «Nuovo appuntamento» finché non lo metti."
                          : "Ancora senza esito."}
                </p>
              </section>

              <dl className="grid gap-inside">
                {[
                  ["Dove", app.sede],
                  ["Con", app.venditore],
                  ["Note", app.note],
                ].map(([k, v]) =>
                  v ? (
                    <div key={k} className="grid grid-cols-[7rem_minmax(0,1fr)] gap-group">
                      <dt className="text-ink-2">{k}</dt>
                      <dd className="whitespace-pre-line font-semibold text-ink-strong">{v}</dd>
                    </div>
                  ) : null,
                )}
                {app.cliente_telefono ? (
                  <div className="grid grid-cols-[7rem_minmax(0,1fr)_auto] items-center gap-group">
                    <dt className="text-ink-2">Telefono</dt>
                    <dd className="font-semibold text-ink-strong">{app.cliente_telefono}</dd>
                    <Button asChild variant="outline" size="icon" className="size-11 md:size-9">
                      <a href={`tel:${app.cliente_telefono_e164 || app.cliente_telefono}`} aria-label={`Chiama ${app.cliente || ""}`}>
                        <Phone />
                      </a>
                    </Button>
                  </div>
                ) : null}
              </dl>

              <MessaggiAutomatici lista={app.messaggi_automatici} />

              {app.lavoro ? (
                <section className="grid gap-group border-t border-line-subtle pt-group" data-testid="dettaglio-lavoro">
                  <h3 className="text-body-strong text-ink-strong">Consegna e montaggio</h3>
                  <LavoroMontaggio
                    lavoro={app.lavoro}
                    onCambiato={() => {
                      carica();
                      onCambiato();
                    }}
                  />
                </section>
              ) : null}

              {app.cliente_id ? <Allegati app={app} onCambiato={carica} /> : null}
            </div>
            <div className="sticky bottom-0 flex flex-wrap items-center gap-inside border-t border-line bg-surface-2 px-card py-3">
              {/* Una visita di Cal si sposta da qui solo col Cal dal CRM acceso. */}
              {inAgenda && !sposta && !delLavoro && (!app.da_cal || app.cal_dal_crm) ? (
                <Button type="button" variant="outline" onClick={() => setSposta(true)} data-testid="dettaglio-sposta">
                  <Clock />
                  Sposta
                </Button>
              ) : null}
              {app.cliente_id ? (
                <Button asChild className="ml-auto">
                  <Link to={`/clienti/${app.cliente_id}`} data-testid="dettaglio-scheda">
                    Apri la scheda
                    <ExternalLink />
                  </Link>
                </Button>
              ) : null}
            </div>
          </>
        )}
        {dialogoConferma}
      </SheetContent>
    </Sheet>
  );
}

/* ── Il giorno vuoto: cosa fare, e chi aspetta una data ───────────────────── */
function GiornoVuoto({ giorno, sedeNome, conClienti, onFissa }) {
  const [aspettano, setAspettano] = useState(null);
  useEffect(() => {
    if (!conClienti) return undefined;
    let vivo = true;
    api
      .get("/crm/clienti", { params: { lista: "tutti" } })
      .then((r) => {
        if (!vivo) return;
        /* In «Nuovo appuntamento» senza una visita fissata: «Showroom · da fissare» (0041). */
        setAspettano((r.data?.righe || []).filter((c) => c.stato === "da_confermare" && !c.appuntamento_senza_esito_at).slice(0, 5));
      })
      .catch(() => vivo && setAspettano(false));
    return () => {
      vivo = false;
    };
  }, [conClienti]);
  const passato_ = giorno < oggi();
  return (
    <div
      className={cn("grid overflow-hidden rounded-xl border border-line bg-surface", conClienti && "md:grid-cols-2")}
      data-testid="cal-vuoto"
    >
      <div className="flex flex-col items-center justify-center gap-3 p-card py-section text-center">
        <span className="inline-flex size-14 items-center justify-center rounded-lg bg-brand-subtle text-brand-ink" aria-hidden>
          <CalendarPlus className="size-7" strokeWidth={1.75} />
        </span>
        <h2 className="font-display text-h3 text-ink-strong">Nessun appuntamento{sedeNome ? ` a ${sedeNome}` : ""}</h2>
        <p className="max-w-sm text-ink-2 first-letter:uppercase">
          {giornoLungo(giorno)} non c'è niente in agenda{sedeNome ? ` a ${sedeNome}` : ""}.
        </p>
        {passato_ ? null : (
          <Button onClick={() => onFissa(null)} data-testid="cal-vuoto-fissa">
            <CalendarPlus />
            Fissa una visita {testaGiorno(giorno).split(" ")[0]} {testaGiorno(giorno).split(" ")[1]}
          </Button>
        )}
      </div>
      {conClienti ? (
        <div className="grid content-start gap-inside border-t border-line-subtle bg-surface-2 p-4 md:border-l md:border-t-0 md:p-card">
          <h3 className="text-body-strong text-ink-strong">Aspettano una data</h3>
          <p className="text-small text-ink-2">I clienti in «Nuovo appuntamento» senza una visita fissata.</p>
          {aspettano === false ? (
            <p className="text-small text-ink-2">Non si è caricato: riapri il giorno per riprovare.</p>
          ) : aspettano === null ? (
            <Skeleton className="h-28 w-full rounded-lg" />
          ) : aspettano.length ? (
            <ul className="grid gap-inside">
              {aspettano.map((c) => (
                <li key={c.id} className="flex items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold text-ink-strong">{c.nome}</span>
                    <span className="block truncate text-small text-ink-2">
                      {[c.fonte, `arrivato ${arrivo(c.created_at)}`].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  {passato_ ? null : (
                    <Button variant="outline" onClick={() => onFissa({ id: c.id, nome: c.nome, telefono: c.telefono })}>
                      Fissa
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-small text-ink-2">Nessuno: ogni cliente nuovo ha già la sua visita.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Legenda() {
  const voci = [
    ["programma", "In programma"],
    ["segnare", "Da segnare"],
    ["fatto", "Fatto"],
    ["saltato", "No show o annullato"],
  ];
  return (
    <ul className="flex flex-wrap items-center gap-x-group gap-y-1 text-small text-ink-2" aria-label="Legenda">
      {voci.map(([k, v]) => (
        <li key={k} className="inline-flex items-center gap-1.5">
          <span className={cn("size-3 rounded-xs border", TONO_CELLA[k])} aria-hidden />
          {v}
        </li>
      ))}
    </ul>
  );
}

const VISTE = [
  { id: "giorno", label: "Giorno" },
  { id: "settimana", label: "Settimana" },
];

/* ── La pagina ─────────────────────────────────────────────────────────────── */
export default function CalendarioPage() {
  const largo = useMedia("(min-width: 768px)");
  const ampio = useMedia("(min-width: 1024px)");
  const { profile } = useAuth() || {};
  const conClienti = vociDi(profile?.ruolo, profile?.sezioni_spente).includes("clienti");
  const [params, setParams] = useSearchParams();
  const vista = params.get("vista") === "giorno" ? "giorno" : "settimana";
  const giorno = /^\d{4}-\d{2}-\d{2}$/.test(params.get("giorno") || "") ? params.get("giorno") : oggi();
  const aperto = params.get("appuntamento");
  const nuovoPer = params.get("nuovo");
  const [persona, setPersona] = useState(TUTTI);
  const [sede, setSede] = useState(TUTTI);
  const [squadra, setSquadra] = useState(TUTTI);
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(null);
  const [nuovo, setNuovo] = useState(false);
  const [clienteIniziale, setClienteIniziale] = useState(null);
  const [busy, setBusy] = useState(false);

  const lunedi = lunediDi(giorno);
  const da = vista === "giorno" ? giorno : lunedi;
  const a = vista === "giorno" ? giorno : piuGiorni(lunedi, 6);
  const settimana = useMemo(() => Array.from({ length: 7 }, (_, i) => piuGiorni(lunedi, i)), [lunedi]);
  /* Sul telefono la striscia dei giorni è sempre quella della settimana: si legge l'intera settimana. */
  const daLeggere = largo ? [da, a] : [lunedi, piuGiorni(lunedi, 6)];

  const aggiorna = useCallback(
    (cambi) => {
      const p = new URLSearchParams(params);
      for (const [k, v] of Object.entries(cambi)) {
        if (v === null || v === undefined) p.delete(k);
        else p.set(k, v);
      }
      setParams(p, { replace: true });
    },
    [params, setParams],
  );

  const carica = useCallback(() => {
    const query = { da: daLeggere[0], a: daLeggere[1] };
    if (persona !== TUTTI) query.venditore = persona;
    if (sede !== TUTTI) query.sede = sede;
    api
      .get("/calendario", { params: query })
      .then((r) => {
        setDati(r.data);
        setErrore(null);
      })
      .catch((e) => setErrore({ testo: erroreDi(e), codice: e?.response?.status || "NET", alle: new Date().toISOString() }));
  }, [daLeggere[0], daLeggere[1], persona, sede]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(carica, [carica]);

  /* Aperto da un link (scheda cliente, «Adesso»): la settimana giusta si trova leggendo l'appuntamento. */
  useEffect(() => {
    if (!aperto || params.get("giorno")) return;
    api
      .get(`/calendario/${aperto}`)
      .then((r) => aggiorna({ giorno: giornoDi(r.data.data_inizio) }))
      .catch(() => {});
  }, [aperto]); // eslint-disable-line react-hooks/exhaustive-deps

  /* «Fissa visita» dalla scheda arriva con ?nuovo=<cliente>; «Nuovo › Appuntamento» con ?nuovo=1, senza cliente. */
  useEffect(() => {
    if (!nuovoPer) return;
    if (nuovoPer === "1") {
      setClienteIniziale(null);
      setNuovo(true);
      aggiorna({ nuovo: null });
      return;
    }
    api
      .get(`/crm/clienti/${nuovoPer}`)
      .then((r) => {
        setClienteIniziale({ id: r.data.cliente.id, nome: r.data.cliente.nome_visualizzato, telefono: r.data.cliente.telefono });
        setNuovo(true);
      })
      .catch(() => {})
      .finally(() => aggiorna({ nuovo: null }));
  }, [nuovoPer]); // eslint-disable-line react-hooks/exhaustive-deps

  const appuntamenti = useMemo(
    () => (dati?.appuntamenti || []).filter((x) => squadra === TUTTI || x.montatori?.includes(squadra)),
    [dati, squadra],
  );
  const perGiorno = useMemo(() => {
    const m = Object.fromEntries(settimana.map((g) => [g, []]));
    for (const x of appuntamenti) {
      const g = giornoDi(x.data_inizio);
      if (m[g]) m[g].push(x);
    }
    return m;
  }, [appuntamenti, settimana]);

  const passo = vista === "giorno" ? 1 : 7;
  const apri = (id) => aggiorna({ appuntamento: id });
  const chiudi = useCallback(() => aggiorna({ appuntamento: null }), [aggiorna]);
  const soloIo = dati && dati.ruolo === "venditore";
  const fissa = (cliente) => {
    setClienteIniziale(cliente);
    setNuovo(true);
  };

  const esitoRapido = async (x, stato) => {
    setBusy(true);
    try {
      await api.patch(`/calendario/${x.id}`, { stato });
      toast.success(stato === "no_show" && x.da_cal ? "Segnato: non è venuto. Il messaggio di recupero va in coda." : "Esito salvato");
      carica();
    } catch (e) {
      toast.error(erroreDi(e));
    } finally {
      setBusy(false);
    }
  };

  /* Il conto della settimana (o del giorno): le visite in showroom, com'è andata. */
  const visibili = vista === "giorno" ? perGiorno[giorno] || appuntamenti.filter((x) => giornoDi(x.data_inizio) === giorno) : appuntamenti;
  const visite = visibili.filter((x) => x.tipo === "visita_showroom" && x.stato !== "annullato");
  const conta = {
    presentati: visite.filter((x) => x.stato === "presentato").length,
    noShow: visite.filter((x) => x.stato === "no_show").length,
    segnare: visite.filter(daSegnare).length,
    programma: visite.filter((x) => IN_AGENDA.includes(x.stato) && !passato(x.data_inizio)).length,
  };
  const dettaglioConto = [
    conta.presentati ? `${conta.presentati} ${conta.presentati === 1 ? "presentata" : "presentate"}` : null,
    conta.noShow ? `${conta.noShow} no show` : null,
    conta.segnare ? `${conta.segnare} da segnare` : null,
    conta.programma ? `${conta.programma} in programma` : null,
  ].filter(Boolean);
  const sedeNome = sede !== TUTTI ? (dati?.sedi || []).find((s) => s.id === sede)?.nome : null;
  const titolo = vista === "giorno" ? giornoLungo(giorno) : "Calendario";
  const sottotitolo =
    vista === "giorno"
      ? ["Calendario", giorno === oggi() ? "oggi" : null, sedeNome ? `solo ${sedeNome}` : null].filter(Boolean).join(" · ")
      : `${intervallo(da, a)} · ${visite.length} ${visite.length === 1 ? "visita" : "visite"} in showroom${dettaglioConto.length ? `: ${dettaglioConto.join(", ")}` : ""}`;

  /* Il giorno, sul computer: una colonna per persona, e la squadra di montaggio a parte. */
  const colonneGiorno = useMemo(() => {
    const m = new Map();
    for (const x of perGiorno[giorno] || []) {
      const squadra_ = x.lavoro_id || x.tipo === "consegna" || x.tipo === "montaggio";
      const k = squadra_ ? "squadra" : x.venditore_id || "nessuno";
      if (!m.has(k))
        m.set(k, {
          chiave: k,
          eventi: [],
          testa: (
            <div className="flex min-h-14 items-center gap-2.5 px-3 py-2">
              <Initials name={squadra_ ? "Squadra Montaggio" : x.venditore || "?"} size={32} muted={squadra_} />
              <span className="min-w-0">
                <span className="block truncate font-bold text-ink-strong">
                  {squadra_ ? "Squadra di montaggio" : x.venditore || "Senza persona"}
                </span>
                <span className="block truncate text-caption font-normal text-ink-2">
                  {squadra_ ? "consegne e montaggi" : x.sede || " "}
                </span>
              </span>
            </div>
          ),
        });
      m.get(k).eventi.push(x);
    }
    return [...m.values()];
  }, [perGiorno, giorno]);

  const colonneSettimana = settimana.map((g) => {
    const eOggi = g === oggi();
    const [nomeG, numero] = testaGiorno(g).split(" ");
    return {
      chiave: g,
      evidenzia: eOggi,
      eventi: perGiorno[g],
      testa: (
        <button
          type="button"
          onClick={() => aggiorna({ vista: "giorno", giorno: g })}
          aria-label={`${giornoLungo(g)}${eOggi ? ", oggi" : ""}: apri il giorno`}
          className="flex min-h-14 w-full items-center justify-center gap-2 hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand"
        >
          <span className={cn(eOggi ? "font-bold text-brand-ink" : "text-ink-2")}>{nomeG}</span>
          <span
            className={cn(
              "inline-flex size-8 items-center justify-center rounded-full font-figure text-h4",
              eOggi ? "bg-brand text-on-brand" : "text-ink-strong",
            )}
          >
            {numero}
          </span>
        </button>
      ),
    };
  });

  const giornata = perGiorno[giorno] || [];
  const daSegnareOggi = giornata.filter(daSegnare);

  const filtri = (
    <div className="grid grid-cols-2 gap-inside md:flex md:flex-wrap md:items-center">
      <Select value={persona} onValueChange={setPersona} disabled={soloIo}>
        <SelectTrigger className="md:h-9 md:w-auto md:min-w-[11rem]" aria-label="Di chi" data-testid="filtro-persona">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={TUTTI}>{soloIo ? "Il mio calendario" : "Tutte le persone"}</SelectItem>
          {(dati?.persone || []).map((p) => (
            <SelectItem key={p.id} value={p.id}>
              {p.id === dati?.io ? `${p.nome} (io)` : p.nome}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={sede} onValueChange={setSede}>
        <SelectTrigger className="md:h-9 md:w-auto md:min-w-[10rem]" aria-label="Sede" data-testid="filtro-sede">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={TUTTI}>Tutte le sedi</SelectItem>
          {(dati?.sedi || []).map((s) => (
            <SelectItem key={s.id} value={s.id}>
              {s.nome}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {dati?.montatori?.length ? (
        <Select value={squadra} onValueChange={setSquadra}>
          <SelectTrigger
            className="col-span-2 md:col-span-1 md:h-9 md:w-auto md:min-w-[10rem]"
            aria-label="Squadra"
            data-testid="filtro-squadra"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TUTTI}>Tutte le squadre</SelectItem>
            {dati.montatori.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
    </div>
  );

  return (
    <section data-testid="page-calendario" className="grid grid-cols-[minmax(0,1fr)] gap-card">
      {largo ? (
        <header className="flex flex-col gap-group xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <h1 className="font-display text-h1 text-ink-strong first-letter:uppercase">{titolo}</h1>
            <p className="mt-micro text-ink-2" data-testid="cal-sottotitolo">
              {sottotitolo}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-inside">
            <Button
              variant="outline"
              size="icon"
              className="size-9"
              onClick={() => aggiorna({ giorno: piuGiorni(giorno, -passo) })}
              aria-label={vista === "giorno" ? "Giorno prima" : "Settimana prima"}
              data-testid="cal-prima"
            >
              <ChevronLeft />
            </Button>
            <Button variant="outline" className="h-9" onClick={() => aggiorna({ giorno: null })} data-testid="cal-oggi">
              Oggi
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="size-9"
              onClick={() => aggiorna({ giorno: piuGiorni(giorno, passo) })}
              aria-label={vista === "giorno" ? "Giorno dopo" : "Settimana dopo"}
              data-testid="cal-dopo"
            >
              <ChevronRight />
            </Button>
            <h2 className="sr-only" aria-live="polite" data-testid="cal-intervallo">
              {vista === "giorno" ? giornoLungo(giorno) : intervallo(da, a)}
            </h2>
            <SegmentedButtons
              voci={VISTE}
              valore={vista}
              onCambia={(v) => aggiorna({ vista: v === "giorno" ? "giorno" : null })}
              prefisso="vista"
              aria-label="Vista"
            />
            <Button className="h-9" onClick={() => fissa(null)} data-testid="btn-nuovo-appuntamento">
              <CalendarPlus />
              Nuovo appuntamento
            </Button>
          </div>
        </header>
      ) : (
        <header className="grid gap-group">
          <div className="flex items-center justify-between gap-group">
            <h1 className="min-w-0 font-display text-h1-mobile text-ink-strong first-letter:uppercase">{titolo}</h1>
            <Button variant="outline" onClick={() => aggiorna({ giorno: null })} data-testid="cal-oggi">
              Oggi
            </Button>
          </div>
          <SegmentedButtons
            voci={VISTE}
            valore={vista}
            onCambia={(v) => aggiorna({ vista: v === "giorno" ? "giorno" : null })}
            prefisso="vista"
            aria-label="Vista"
            className="w-full"
          />
          <div className="flex items-center justify-between gap-inside">
            <Button
              variant="ghost"
              size="icon"
              className="size-11"
              onClick={() => aggiorna({ giorno: piuGiorni(giorno, -passo) })}
              aria-label={vista === "giorno" ? "Giorno prima" : "Settimana prima"}
              data-testid="cal-prima"
            >
              <ChevronLeft />
            </Button>
            <h2
              className="min-w-0 truncate text-center font-bold text-ink-strong first-letter:uppercase"
              aria-live="polite"
              data-testid="cal-intervallo"
            >
              {vista === "giorno" ? giornoLungo(giorno) : intervallo(lunedi, piuGiorni(lunedi, 6))}
            </h2>
            <Button
              variant="ghost"
              size="icon"
              className="size-11"
              onClick={() => aggiorna({ giorno: piuGiorni(giorno, passo) })}
              aria-label={vista === "giorno" ? "Giorno dopo" : "Settimana dopo"}
              data-testid="cal-dopo"
            >
              <ChevronRight />
            </Button>
          </div>
          <ol className="grid grid-cols-7 gap-1" aria-label="I giorni della settimana">
            {settimana.map((g) => {
              const [nomeG, numero] = testaGiorno(g).split(" ");
              const scelto = vista === "giorno" ? g === giorno : g === oggi();
              const punti = (perGiorno[g] || []).slice(0, 3);
              return (
                <li key={g}>
                  <button
                    type="button"
                    onClick={() => aggiorna({ vista: "giorno", giorno: g })}
                    aria-label={`${giornoLungo(g)}: ${perGiorno[g]?.length || 0} appuntamenti`}
                    className={cn(
                      "flex min-h-16 w-full flex-col items-center justify-center gap-0.5 rounded-md",
                      "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
                      scelto ? "bg-brand text-on-brand" : "text-ink hover:bg-surface-3",
                    )}
                  >
                    <span className="text-caption font-medium">{nomeG}</span>
                    <span className="font-figure text-h4">{numero}</span>
                    <span className="flex h-1.5 gap-0.5" aria-hidden>
                      {punti.map((x) => (
                        <span
                          key={x.id}
                          className={cn(
                            "size-1.5 rounded-full",
                            scelto
                              ? "bg-on-brand"
                              : statoCella(x).chiave === "saltato"
                                ? "bg-danger"
                                : statoCella(x).chiave === "segnare"
                                  ? "bg-warning"
                                  : "bg-brand",
                          )}
                        />
                      ))}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </header>
      )}

      <div className="flex flex-col gap-group lg:flex-row lg:items-center lg:justify-between">
        {filtri}
        {largo && vista === "settimana" ? <Legenda /> : null}
      </div>

      <NuovoAppuntamentoDialog
        open={nuovo}
        onOpenChange={setNuovo}
        sedi={dati?.sedi || []}
        giornoIniziale={giorno}
        clienteIniziale={clienteIniziale}
        calDalCrm={dati ? Boolean(dati.cal_dal_crm) : undefined}
        onCreato={(app) => {
          toast.success(app.da_cal ? "Fissata, anche su Cal: online quell'orario non si prenota più." : "Appuntamento fissato");
          aggiorna({ giorno: giornoDi(app.data_inizio), appuntamento: app.id });
          carica();
        }}
      />

      {errore ? (
        <div className="rounded-xl border border-line bg-surface" data-testid="cal-errore">
          <ErrorState
            title="Non riusciamo a caricare il calendario"
            code={`CAL-${errore.codice} · ${ora(errore.alle)}`}
            className="py-section"
            actions={<Button onClick={carica}>Riprova</Button>}
          >
            {errore.codice === "NET" ? "Il gestionale non ha ricevuto risposta: controlla la connessione e riprova." : `${errore.testo}.`}
          </ErrorState>
        </div>
      ) : dati === null ? (
        <Skeleton className="h-[32rem] w-full rounded-xl" aria-label="Caricamento calendario" />
      ) : vista === "giorno" && !giornata.length ? (
        <GiornoVuoto giorno={giorno} sedeNome={sedeNome} conClienti={conClienti} onFissa={fissa} />
      ) : largo && vista === "settimana" ? (
        <div data-testid="cal-settimana">
          <Griglia colonne={colonneSettimana} oraPx={64} onApri={apri} conLinea={(c) => c.chiave === oggi()} />
        </div>
      ) : largo ? (
        <div className={cn("grid gap-card", ampio && "grid-cols-[minmax(0,1fr)_20rem]")} data-testid="cal-giorno">
          <Griglia colonne={colonneGiorno} oraPx={56} onApri={apri} conLinea={() => giorno === oggi()} />
          <aside className="grid content-start gap-card">
            <section className="rounded-xl border border-line bg-surface p-card" data-testid="cal-giornata">
              <h2 className="font-display text-card-title text-ink-strong">La giornata</h2>
              <dl className="mt-group grid gap-inside">
                {[
                  [
                    "Visite in showroom",
                    giornata.filter((x) => x.tipo === "visita_showroom" && x.stato !== "annullato").length,
                    "text-ink-strong",
                  ],
                  [
                    "Presentate",
                    giornata.filter((x) => x.tipo === "visita_showroom" && x.stato === "presentato").length,
                    "text-success-ink",
                  ],
                  ["Da segnare", daSegnareOggi.length, "text-warning-ink"],
                  [
                    "Rilievi e montaggi",
                    giornata.filter((x) => x.tipo !== "visita_showroom" && x.stato !== "annullato").length,
                    "text-ink-strong",
                  ],
                ].map(([k, n, tono]) => (
                  <div key={k} className="flex items-center justify-between gap-group">
                    <dt className="text-ink-2">{k}</dt>
                    <dd className={cn("font-bold tabular-nums", tono)}>{n}</dd>
                  </div>
                ))}
              </dl>
              {daSegnareOggi.length ? (
                <Button
                  variant="outline"
                  className="mt-group w-full border-warning-line bg-warning-bg text-warning-ink hover:bg-warning-bg"
                  onClick={() => apri(daSegnareOggi[0].id)}
                >
                  Segna la visita di {(daSegnareOggi[0].cliente || "").split(" ")[0] || "chi è passato"}
                </Button>
              ) : null}
            </section>
          </aside>
        </div>
      ) : (
        <div className="grid gap-card" data-testid="cal-agenda">
          {(vista === "giorno" ? [giorno] : settimana.filter((g) => perGiorno[g].length || g === oggi())).map((g) => (
            <section key={g} className="grid gap-inside" aria-label={giornoLungo(g)}>
              <h3 className={cn("font-bold first-letter:uppercase", g === oggi() ? "text-brand-ink" : "text-ink-strong")}>
                {g === oggi() && vista === "settimana" ? `Oggi, ${giornoLungo(g)}` : giornoLungo(g)}
              </h3>
              {perGiorno[g].length ? (
                <ul className="grid gap-inside">
                  {perGiorno[g].map((x) => (
                    <RigaAgenda key={x.id} a={x} onApri={apri} onEsito={esitoRapido} busy={busy} />
                  ))}
                </ul>
              ) : (
                <p className="rounded-lg border border-dashed border-line-chip px-4 py-3 text-ink-2">Niente in agenda</p>
              )}
            </section>
          ))}
          {vista === "settimana" && !appuntamenti.length ? <p className="text-ink-2">Nessun appuntamento in questa settimana.</p> : null}
        </div>
      )}

      <Dettaglio id={aperto} onChiudi={chiudi} onCambiato={carica} />
    </section>
  );
}
